/**
 * Выбор вкладки-лидера (CLIENT_WEB.md §6.7, §9.2 п. 5).
 *
 * Зачем: store `pending_ops` общий для всех вкладок одного профиля браузера.
 * Без лидера две вкладки отправили бы одну операцию одновременно — от дублей
 * спасает только `client_message_id`, но лишние запросы и гонки остаются.
 * Лидер также держит единственное WebSocket-соединение.
 *
 * Механизм: Web Locks API — вкладка бесконечно удерживает лок `app-leader`;
 * при её закрытии лок освобождается и его забирает следующая. Если API нет,
 * лидером считается каждая вкладка (деградация без потери функций).
 */

const LOCK_NAME = 'dvizh-app-leader'

export const TAB_ID = `tab-${Math.random().toString(36).slice(2, 10)}`

type LeadershipListener = (isLeader: boolean) => void

const listeners = new Set<LeadershipListener>()
let isLeader = false
let started = false
let release: (() => void) | null = null

function setLeader(value: boolean): void {
  if (isLeader === value) return
  isLeader = value
  for (const listener of listeners) {
    try {
      listener(value)
    } catch {
      /* подписчик не должен ломать выбор лидера */
    }
  }
}

/** Начинает борьбу за лидерство. Идемпотентна. */
export function startLeaderElection(): void {
  if (started) return
  started = true

  const locks = navigator.locks as LockManager | undefined
  if (!locks?.request) {
    // Без Web Locks считаем вкладку лидером: функции важнее оптимизации.
    setLeader(true)
    return
  }

  void locks.request(LOCK_NAME, () => {
    setLeader(true)
    return new Promise<void>((resolve) => {
      release = () => {
        setLeader(false)
        resolve()
      }
    })
  })
}

/**
 * Добровольно уступает лидерство (выход, закрытие вкладки).
 *
 * Снимает признак и в запасном пути без Web Locks: там `release` не задан,
 * и без явного сброса вкладка осталась бы «лидером» после выхода, продолжая
 * считать очередь своей.
 */
export function releaseLeadership(): void {
  release?.()
  release = null
  started = false
  setLeader(false)
}

export function isLeaderTab(): boolean {
  return isLeader
}

export function onLeadershipChange(listener: LeadershipListener): () => void {
  listeners.add(listener)
  listener(isLeader)
  return () => listeners.delete(listener)
}

/* ===================================================================== */
/* Канал между вкладками                                                  */
/* ===================================================================== */

export type TabMessage =
  | { type: 'logout' }
  | { type: 'owner_changed'; user_id: string }
  | { type: 'force_reload' }
  | { type: 'replica_changed' }

const CHANNEL_NAME = 'dvizh-app'

let channel: BroadcastChannel | null = null

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null
  channel ??= new BroadcastChannel(CHANNEL_NAME)
  return channel
}

export function postTabMessage(message: TabMessage): void {
  getChannel()?.postMessage(message)
}

export function onTabMessage(listener: (message: TabMessage) => void): () => void {
  const bc = getChannel()
  if (!bc) return () => undefined

  const handler = (event: MessageEvent): void => listener(event.data as TabMessage)
  bc.addEventListener('message', handler)
  return () => bc.removeEventListener('message', handler)
}
